import { PressEvent } from '@react-types/shared';
import { useCallback } from 'react';
import { useUnit } from 'state/unit';
import { isSegmentable } from 'utils/audio';
import { DrawableAudioPlayer } from 'utils/audio/types';
import preparePlayUrl from 'utils/destUrl';
import usePodcastDestination, {
  UsePodcastDestinationConfig,
  UsePodcastDestinationResult,
} from './usePodcastDestination';

interface UsePlaybackAwarePodcastDestinationConfig
  extends UsePodcastDestinationConfig,
    Pick<React.HTMLAttributes<HTMLAnchorElement>, 'onClick'> {
  player?: DrawableAudioPlayer;
}

type UsePreparePlayDestinationUrlResult = UsePodcastDestinationResult & {
  anchorProps: Pick<React.HTMLAttributes<HTMLAnchorElement>, 'onClick'>;
};

export default function usePreparePlayDestinationUrl({
  onClick,
  onPress,
  player,
}: UsePlaybackAwarePodcastDestinationConfig = {}): UsePreparePlayDestinationUrlResult {
  const unit = useUnit();

  const destUrl = unit?.episode.destUrl;
  const isPlayAppDestUrl = unit?.episode.isPlayAppDestUrl;

  // Play app URLs show the base URL on hover, but when clicked a timestamp query
  // parameter is added so that playback begins where the user left off.  Handling
  // it this way avoids having to update the URL written to the anchor tag in the DOM
  // as playback progresses.
  const requiresManualRedirect = isPlayAppDestUrl && destUrl;

  const getTimestamp = useCallback(() => {
    if (player) {
      const timestamp = isSegmentable(player)
        ? player.rawCurrentTime()
        : player.currentTime();
      return timestamp;
    }
    return 0;
  }, [player]);

  // react-aria's anchor handling does not prevent default and does not expose
  // the event's preventDefault function in its PressEvent.  Passing onClick will
  // generate a warning in the console, but is the easiest way to prevent Play pages
  // from opening before the timestamp is appended
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      onClick?.(e);
    },
    [onClick],
  );

  const handlePress = useCallback(
    (e: PressEvent) => {
      onPress?.(e);

      if (!requiresManualRedirect) {
        // In some instances on mobile browsers, clicking on the PodcastDestinationLink
        // does not trigger the default link behavior, so force the link to open here instead.
        window.open(destUrl, '__blank', 'noopener');
        return;
      }

      const url = preparePlayUrl(destUrl, getTimestamp());

      window.open(url, '__blank', 'noopener');
    },
    [destUrl, getTimestamp, onPress, requiresManualRedirect],
  );

  const { anchorProps } = usePodcastDestination({ onPress: handlePress });

  return {
    anchorProps: {
      ...anchorProps,
      onClick: handleClick,
    },
  };
}
